import urljoin from 'url-join';
import { sites } from '@immediate_media/site-config';

/**
 * @typedef {import('axios').AxiosResponse<any, any>} AxiosResponse
 */

/**
 * Get the filters to be used for one of the list endpoints.
 */
const getFilters = (filters, siteKey, appendClient, appendLanguage) => {
  const obj = Object.keys(filters).filter(filter => filter) // Remove undefined
  .reduce((acc, filter) => {
    acc[filter] = filters[filter];
    return acc;
  }, {});

  // The search API currently uses `sitekey` instead of `client`. The if
  // statement can go once that API is made consistent
  if (!obj.sitekey) {
    obj.client = siteKey; // Add client filter to all requests
  }

  // Due to constraints on the V4 and V5 search api we are ensuring search param
  // is trimmed to 250 characters this ensures that we do not receive a
  // bad response when validated by the api the API Limit is 280
  if (obj !== null && obj !== void 0 && obj.search && (obj === null || obj === void 0 ? void 0 : obj.search.length) > 250) {
    const searchTermTrimmedToMaxLength = obj.search.substring(0, 250);
    const searchTermTrimmedToLastWord = searchTermTrimmedToMaxLength.substr(0, Math.min(searchTermTrimmedToMaxLength.length, searchTermTrimmedToMaxLength.lastIndexOf(' ')));
    obj.search = searchTermTrimmedToLastWord;
  }
  if (appendClient) {
    delete obj.sitekey;
  }
  if (appendLanguage) {
    const {
      language = 'en'
    } = sites[siteKey];
    const [lang] = language.split('-');
    obj.lang = lang;
  }
  return obj;
};

// V5 of search requires that the client is appended to the endpoint path
const getEndpoint = (endpoint, siteKey, appendClient) => {
  if (appendClient) {
    return urljoin(endpoint, siteKey);
  }
  return endpoint;
};

/**
 * Register the functions for each API endpoint.
 */
export default ((client, env, siteKey, {
  endpoint,
  mocks
}, appendClientToEndpoint, appendLanguage) => {
  const functions = {
    /**
     * Get a resource.
     * @param {string | number} id
     * @param {Record<string, string>} [params]
     * @returns {Promise<AxiosResponse>}
     */
    get: (id, params = {}) => {
      if (!id) {
        throw new TypeError('An id must be given for the entity to get, perhaps you want the filter() function.');
      }
      return client.get(urljoin(endpoint, id.toString()), {
        params
      });
    },
    /**
     * Create a resource.
     * @param {any} data
     * @returns {Promise<AxiosResponse>}
     */
    create: data => {
      if (!data) {
        throw new TypeError('Some data must be given to create an entity.');
      }
      return client.post(endpoint, {
        data
      });
    },
    /**
     * Delete a resource.
     * @param {string | number} id
     * @returns {Promise<AxiosResponse>}
     */
    delete: id => {
      if (!id) {
        throw new TypeError('An id must be given for the entity to delete.');
      }
      return client.delete(urljoin(endpoint, id.toString()));
    },
    /**
     * Update a resource.
     * @param {string | number} id
     * @param {any} data
     * @returns {Promise<AxiosResponse>}
     */
    update: (id, data) => {
      if (!id) {
        throw new TypeError('An id must be given for the entity to update.');
      }
      if (!data) {
        throw new TypeError('Some data must be given to update an entity.');
      }
      return client.put(urljoin(endpoint, id.toString()), {
        data
      });
    },
    /**
     * Filter resources.
     * @param {Record<string, any>} filters
     * @returns {Promise<AxiosResponse>}
     */
    filter: (filters = {}) => client.get(getEndpoint(endpoint, siteKey, appendClientToEndpoint), {
      params: getFilters(filters, siteKey, appendClientToEndpoint, appendLanguage)
    }),
    /**
     * Find a resource.
     * @param {Record<string, any>} filters
     * @returns {Promise<AxiosResponse>}
     */
    find: async (filters = {}) => {
      var _data$hydraMember;
      const data = await client.get(getEndpoint(endpoint, siteKey, appendClientToEndpoint), {
        params: getFilters({
          ...filters,
          limit: 1
        }, siteKey, appendClientToEndpoint, appendLanguage)
      });
      return data === null || data === void 0 || (_data$hydraMember = data['hydra:member']) === null || _data$hydraMember === void 0 ? void 0 : _data$hydraMember[0];
    }
  };

  // Replace any mocked functions
  if (mocks) {
    const mockFunctions = mocks({
      ...functions
    }, env);
    Object.keys(mockFunctions).forEach(functionName => {
      functions[functionName] = mockFunctions[functionName];
    });
  }
  return functions;
});